import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Search, X } from 'lucide-react';

const getOptionStateColorClass = (stateOrStatus) => {
  const raw = String(stateOrStatus || '').toLowerCase();
  if (raw === 'active' || raw === 'current') return 'text-green-500';
  if (raw === 'future' || raw === 'unreleased') return 'text-yellow-500';
  if (raw === 'closed' || raw === 'past' || raw === 'done' || raw === 'released') return 'text-red-500';
  return 'text-yellow-500';
};

function MultiSelectCheckboxDropdown({
  options = [],
  onSelect,
  placeholder = 'Select',
  selectedValues = [],
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
  reference,
  width = 'md',
  height = 'md',
  textSize = 'sm',
  title = '',
  disabled = false,
  showSelectAll = false,
  renderOptionLabel = null,
  onOptionHover = null,
  onOptionMouseLeave = null,
  optionDataAttribute = false,
  showSearch = false,
}) {
  const [isOpen, setIsOpen] = useState(externalIsOpen || false);
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useSelector((state) => state.theme.theme);
  const internalDropdownRef = useRef(null);
  const dropdownRef = reference || internalDropdownRef;

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allSelected = filteredOptions.length > 0 && selectedValues.length === filteredOptions.length;

  const toggleDropdown = () => {
    if (disabled) return;
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    if (externalSetIsOpen) externalSetIsOpen(newOpenState);
    if (!newOpenState) {
      setSearchTerm('');
    }
  };

  const handleSelect = (option) => {
    if (option.disabled) return;

    if (option.value === '__all__') {
      onSelect(allSelected ? [] : filteredOptions.map((opt) => opt.value));
      return;
    }

    if (selectedValues.includes(option.value)) {
      onSelect(selectedValues.filter((value) => value !== option.value));
    } else {
      onSelect([...selectedValues, option.value]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (externalSetIsOpen) externalSetIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef, externalSetIsOpen]);

  const getWidthClass = () => {
    switch (width) {
      case 'xs':
        return 'w-20';
      case 'sm':
        return 'w-32';
      case 'md':
        return 'w-48';
      case 'lg':
        return 'w-64';
      case 'xl':
        return 'w-80';
      default:
        return width.startsWith('w-') ? width : 'w-48';
    }
  };

  const getHeightClass = () => {
    switch (height) {
      case 'xs':
        return 'h-7';
      case 'sm':
        return 'h-8';
      case 'md':
        return 'h-9';
      case 'lg':
        return 'h-10';
      default:
        return height.startsWith('h-') ? height : 'h-9';
    }
  };

  const getTextSizeClass = () => {
    switch (textSize) {
      case 'xs':
        return 'text-xs';
      case 'sm':
        return 'text-sm';
      case 'base':
        return 'text-base';
      default:
        return textSize.startsWith('text-') ? textSize : 'text-sm';
    }
  };

  const buttonLabel = (() => {
    if (!selectedValues.length) return placeholder;
    if (selectedValues.length === 1) {
      const match = options.find((opt) => opt.value === selectedValues[0]);
      return match?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  })();

  return (
    <div ref={dropdownRef} className={`relative inline-block text-left ${getWidthClass()}`}>
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        aria-label={title || placeholder}
        className={`inline-flex items-center justify-between w-full ${getHeightClass()} px-3 ${getTextSizeClass()} rounded-md border transition-colors duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${
            theme === 'light'
              ? 'bg-white border-[#C1D5FF] text-[#24527A] hover:border-[#066FD1]'
              : 'bg-[#182433] border-[#25384F] text-[#D9E4F1] hover:border-[#066FD1]'
          }`}
      >
        <span className="truncate">{buttonLabel}</span>
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute z-[50] mt-1 w-full min-w-[220px] rounded-lg shadow-lg flex flex-col max-h-64 overflow-hidden
            ${theme === 'light' ? 'bg-white border border-[#C1D5FF]' : 'bg-[#182433] border border-[#25384F]'}`}
        >
          {showSearch && (
            <div className={`border-b ${theme === 'light' ? 'bg-[#FFFFFF] border-[#E5E5E5]' : 'bg-[#182433] border-[#1F2F41]'}`}>
              <div className={`relative ${theme === 'light' ? 'bg-[#FFFFFF]' : 'bg-[#182433]'}`}>
                <div className="absolute top-0 bottom-0 left-0 flex items-center justify-center w-8 pointer-events-none">
                  <Search
                    size={16}
                    className={theme === 'light' ? 'text-[#24527A]' : 'text-[#CED5E3]'}
                  />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className={`w-full text-sm py-2 pr-8 focus:outline-none border-none ${
                    theme === 'light' ? 'text-[#24527A]' : 'text-[#D9E4F199]'
                  }`}
                  style={{
                    textIndent: '2rem',
                    backgroundColor: theme === 'light' ? '#FFFFFF' : '#1e293b',
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="overflow-y-auto flex-1 py-1">
            {showSelectAll && filteredOptions.length > 1 && (
              <label
                className={`flex items-center px-4 py-2 cursor-pointer ${
                  theme === 'light' ? 'hover:bg-[#F5F5F5]' : 'hover:bg-[#1E2B3A]'
                }`}
              >
                <input
                  type="checkbox"
                  className="mr-3 w-4 h-4"
                  checked={allSelected}
                  onChange={() => handleSelect({ value: '__all__' })}
                />
                <span
                  className={`text-sm font-medium ${
                    theme === 'light' ? 'text-[#24527A]' : 'text-white'
                  }`}
                >
                  Select All
                </span>
              </label>
            )}
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  {...(optionDataAttribute ? { 'data-project-id': option.value } : {})}
                  className={`flex items-start px-4 py-2 cursor-pointer ${
                    theme === 'light' ? 'hover:bg-[#F5F5F5]' : 'hover:bg-[#1E2B3A]'
                  } ${option.disabled ? 'opacity-50 pointer-events-none' : ''}`}
                  onMouseEnter={() => onOptionHover?.(option.value)}
                  onMouseLeave={() => onOptionMouseLeave?.()}
                >
                  <input
                    type="checkbox"
                    className="mr-3 mt-0.5 w-4 h-4 flex-shrink-0"
                    checked={selectedValues.includes(option.value)}
                    disabled={option.disabled}
                    onChange={() => handleSelect(option)}
                  />
                  <span
                    className={`flex-1 text-sm ${
                      theme === 'light' ? 'text-[#24527A]' : 'text-white'
                    }`}
                  >
                    {renderOptionLabel ? renderOptionLabel(option) : option.label}
                    {option.state && (
                      <span className={`ml-2 text-xs ${getOptionStateColorClass(option.state)}`}>
                        ({option.state})
                      </span>
                    )}
                    {option.status && !option.state && (
                      <span className={`ml-2 text-xs ${getOptionStateColorClass(option.status)}`}>
                        ({option.status})
                      </span>
                    )}
                  </span>
                </label>
              ))
            ) : (
              <div className={`px-4 py-2 text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                No options available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

MultiSelectCheckboxDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      state: PropTypes.string,
      status: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ),
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  reference: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  width: PropTypes.string,
  height: PropTypes.string,
  textSize: PropTypes.string,
  title: PropTypes.string,
  disabled: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  renderOptionLabel: PropTypes.func,
  onOptionHover: PropTypes.func,
  onOptionMouseLeave: PropTypes.func,
  optionDataAttribute: PropTypes.bool,
  showSearch: PropTypes.bool,
};

export default MultiSelectCheckboxDropdown;
